import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Package, Calendar, Thermometer, AlertTriangle, 
  Check, Snowflake, Clock, FileText, QrCode, Download
} from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const LottoPubblicoPage = ({ lottoId }) => {
  const [lotto, setLotto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  
  // Estrai tenant dalla URL (query parameter)
  const urlParams = new URLSearchParams(window.location.search);
  const tenantDb = urlParams.get('tenant');

  useEffect(() => {
    if (lottoId) {
      loadLotto();
    }
  }, [lottoId]);

  const loadLotto = async () => {
    try {
      setLoading(true);
      // Endpoint pubblico - passa tenant come query parameter
      const tenantParam = tenantDb ? `?tenant=${tenantDb}` : '';
      const res = await axios.get(`${API}/gestione/haccp/lotto-pubblico/${lottoId}${tenantParam}`);
      setLotto(res.data);
    } catch (err) {
      console.error('Errore caricamento lotto:', err);
      setError('Lotto non trovato o non accessibile');
    } finally {
      setLoading(false);
    }
  };

  const downloadSchedaPdf = async () => {
    try {
      setDownloading(true);
      const res = await axios.get(`${API}/gestione/haccp/genera-scheda-pdf/${lottoId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `scheda_lotto_${lotto?.numero_lotto?.replace('/', '-') || 'prodotto'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Errore download PDF:', error);
      alert('Errore nel download della scheda PDF');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Caricamento scheda prodotto...</p>
        </div>
      </div>
    );
  }

  if (error || !lotto) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <AlertTriangle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Errore</h1>
          <p className="text-gray-600">{error || 'Lotto non trovato'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
              <Package className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Scheda Prodotto HACCP</h1>
              <p className="text-sm text-blue-600">Tracciabilità Alimentare</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {/* Card Principale */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Header Prodotto */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-blue-200 text-sm mb-1">Lotto N°</p>
                <p className="text-3xl font-bold">{lotto.numero_lotto}</p>
              </div>
              {lotto.abbattimento && (
                <div className="bg-white/20 rounded-xl px-3 py-2 flex items-center gap-2">
                  <Snowflake className="h-5 w-5" />
                  <span className="text-sm font-medium">Abbattuto</span>
                </div>
              )}
            </div>
            <h2 className="text-xl font-semibold mt-4">{lotto.ricetta_nome}</h2>
          </div>

          {/* Dati Produzione */}
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                  <Calendar className="h-4 w-4" />
                  Data Produzione
                </div>
                <p className="text-lg font-semibold text-gray-900">{lotto.data_produzione}</p>
              </div>
              <div className="bg-orange-50 rounded-xl p-4">
                <div className="flex items-center gap-2 text-orange-600 text-sm mb-1">
                  <Clock className="h-4 w-4" />
                  Da consumare entro
                </div>
                <p className="text-lg font-semibold text-orange-700">{lotto.data_scadenza}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-gray-500 text-sm mb-1">Quantità</p>
                <p className="text-lg font-semibold text-gray-900">{lotto.quantita} {lotto.unita_misura}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                  <Thermometer className="h-4 w-4" />
                  Conservazione
                </div>
                <p className="text-lg font-semibold text-gray-900">
                  {lotto.abbattimento ? '-18°C' : '+4°C'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Abbattimento */}
        {lotto.abbattimento && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Snowflake className="h-5 w-5 text-blue-500" />
              Dati Abbattimento
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Inizio</p>
                <p className="font-medium">{lotto.abbattimento_inizio || 'N/D'}</p>
              </div>
              <div>
                <p className="text-gray-500">Fine</p>
                <p className="font-medium">{lotto.abbattimento_fine || 'N/D'}</p>
              </div>
              <div className="col-span-2">
                <p className="text-gray-500">Temperatura</p>
                <p className="font-medium">{lotto.temperatura_abbattimento}°C</p>
              </div>
            </div>
          </div>
        )}

        {/* Ingredienti */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="h-5 w-5 text-green-500" />
            Ingredienti e Tracciabilità
          </h3>
          <div className="space-y-3">
            {(lotto.ingredienti || []).map((ing, idx) => (
              <div key={idx} className="bg-gray-50 rounded-xl p-4">
                <p className="font-semibold text-gray-900 mb-2">{ing.nome}</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-gray-500">Fornitore: </span>
                    <span className="font-medium">{ing.fornitore || 'N/D'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Lotto: </span>
                    <span className="font-medium">{ing.lotto || 'N/D'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">DDT n°: </span>
                    <span className="font-medium">{ing.ddt || 'N/D'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Data DDT: </span>
                    <span className="font-medium">{ing.data_ddt || 'N/D'}</span>
                  </div>
                </div>
              </div>
            ))}
            {(!lotto.ingredienti || lotto.ingredienti.length === 0) && (
              <p className="text-gray-500 text-center py-4">Nessun ingrediente registrato</p>
            )}
          </div>
        </div>

        {/* Allergeni */}
        {lotto.allergeni && lotto.allergeni.length > 0 && (
          <div className="bg-orange-50 rounded-2xl shadow-lg p-6 border border-orange-200">
            <h3 className="font-semibold text-orange-900 mb-4 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              Allergeni
            </h3>
            <div className="flex flex-wrap gap-2">
              {lotto.allergeni.map((all, idx) => (
                <span 
                  key={idx} 
                  className="px-3 py-1.5 bg-orange-100 text-orange-800 rounded-full text-sm font-medium"
                >
                  {all}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Pulsante Download PDF */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <button
            onClick={downloadSchedaPdf}
            disabled={downloading}
            className="w-full flex items-center justify-center gap-3 py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-xl font-semibold text-lg transition-all disabled:opacity-50"
          >
            {downloading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                Download in corso...
              </>
            ) : (
              <>
                <Download className="h-6 w-6" />
                Scarica Scheda PDF
              </>
            )}
          </button>
        </div>

        {/* Footer */}
        <div className="text-center text-gray-400 text-xs py-4">
          <p>Scheda generata automaticamente</p>
          <p>Sistema HACCP - Tracciabilità Alimentare</p>
        </div>
      </div>
    </div>
  );
};

export default LottoPubblicoPage;
